/*
 * stepEnglish — turn a Playwright step into a sentence a tester can read.
 *
 * Every step list in the app shows two things now: what the step DOES, in
 * plain English, and the actual Playwright instruction underneath it. Both are
 * needed — the sentence is what a reviewer reads, the instruction is what you
 * debug with when a selector goes stale.
 *
 * This lived inside GenerationView, so only the generation preview benefited.
 * The run view, the board rows and the execution evidence all showed the raw
 * form ("click #add-btn") and nothing else.
 */

/* Steps that are a whole interaction rather than one action on one element.
   They carry no selector, so the generic "<action> <element>" phrasing reads
   as nonsense ("esignSingle an element"). */
const WHOLE_STEP_ENGLISH = {
  esignsingle:
    "Complete the eSignature — sign as the logged-in user (Performed By)",
  esigndouble:
    "Complete the eSignature — sign as the logged-in user, then as the verifier",
  esign: "Complete the eSignature dialog",
  closedialog: "Close the dialog and go back to the grid",
};

/* A readable name for whatever a selector points at. */
export function friendlySelector(sel) {
  if (!sel) return "an element";

  // Priority: label/text references first — these carry the field name.
  // label:text-is("State"), label:has-text("Name")
  const labelTextMatch = sel.match(
    /label[^)]*(?:text-is|has-text)\s*\(\s*["']([^"']+)["']\s*\)/i,
  );
  if (labelTextMatch) {
    const name = labelTextMatch[1];
    if (/p-dropdown|p-multiselect|p-cascadeselect|p-treeselect/i.test(sel)) {
      return `the "${name}" dropdown`;
    }
    return `the "${name}" field`;
  }

  // :has-text("Save"), :text("Reset"), text="Submit"
  const hasTextMatch = sel.match(
    /(?::has-text|:text-is|:text|text=)\s*\(?["']([^"']+)["']\)?/i,
  );
  if (hasTextMatch) {
    const text = hasTextMatch[1];
    if (/button/i.test(sel)) return `the "${text}" button`;
    if (/^tr[:.[]/i.test(sel) || /\btr\b/.test(sel)) return `the "${text}" row`;
    if (/^td[:.[]/i.test(sel) || /\btd\b/.test(sel)) return `the "${text}" cell`;
    if (/p-menuitem|\[role="menuitem"\]/i.test(sel)) return `the "${text}" menu item`;
    return `"${text}"`;
  }

  const ariaMatch = sel.match(/\[aria-label=["']([^"']+)["']\]/);
  if (ariaMatch) return `the "${ariaMatch[1]}" button`;

  if (sel.startsWith("#")) return `the "${sel.slice(1)}" field`;

  const phMatch = sel.match(/\[placeholder=["']([^"']+)["']\]/);
  if (phMatch) return `the "${phMatch[1]}" input`;

  const nameMatch = sel.match(/\[name=["']([^"']+)["']\]/);
  if (nameMatch) return `the "${nameMatch[1]}" field`;

  if (/p-toast|p-message-error/i.test(sel)) return "the message banner";
  if (/\.p-error/i.test(sel)) return "the validation error message";
  if (/\.p-invalid/i.test(sel)) return "the invalid field indicator";
  if (/p-dialog-mask/i.test(sel)) return "the dialog overlay";
  if (/p-dialog|\[role="dialog"\]/i.test(sel)) return "the dialog";
  if (/p-menuitem|\[role="menu/i.test(sel)) return "the action menu";
  if (/\.p-dropdown/i.test(sel) && sel.length < 30) return "the dropdown";

  if (/^button$/i.test(sel)) return "a button";
  if (/^input$/i.test(sel)) return "an input field";
  if (/^select$/i.test(sel)) return "a dropdown";

  if (sel.length > 50) return "a field";
  return `the "${sel}" element`;
}

/* The sentence. */
export function stepToEnglish(s) {
  if (!s || typeof s !== "object") return "Perform a step";
  if (s.description) return s.description;

  const action = (s.action || "").toLowerCase();
  if (WHOLE_STEP_ENGLISH[action]) return WHOLE_STEP_ENGLISH[action];

  const field = s.field_label
    ? `the "${s.field_label}" field`
    : s.selector
      ? friendlySelector(s.selector)
      : "an element";
  const masked = !!s.masked;
  const raw = s.value != null && s.value !== "" ? String(s.value) : null;
  const val = masked && raw ? "••••••••" : raw;

  switch (action) {
    case "fill":
      return val ? `Enter "${val}" in ${field}` : `Clear ${field}`;
    case "type":
      return val ? `Type "${val}" in ${field}` : `Type in ${field}`;
    case "click":
      return `Click ${field}`;
    case "press":
      return val ? `Press the ${val} key` : "Press a key";
    case "selectoption":
    case "selectdropdown":
      return val ? `Choose "${val}" in ${field}` : `Choose an option in ${field}`;
    case "expectvisible":
      return `Check that ${field} is shown`;
    case "expecthidden":
      return `Check that ${field} is gone`;
    case "expecttext":
      return val ? `Check that "${val}" is shown` : "Check the text";
    case "expecturlcontains":
      return val ? `Check the page address contains "${val}"` : "Check the page address";
    case "expectdisabled":
      return `Check that ${field} is disabled`;
    case "expectenabled":
      return `Check that ${field} is enabled`;
    case "expectfocused":
      return `Check that the cursor is in ${field}`;
    case "expectnotfocused":
      return `Check that the cursor is not in ${field}`;
    case "expectvalue":
      return val ? `Check that ${field} reads "${val}"` : `Check that ${field} is empty`;
    case "pickdate": {
      /* The date is worked out when the test runs ("+1 month 17:00"). */
      const named = /Open calendar:\s*([^"\]]+)/i.exec(s.selector || "");
      const where = s.field_label || (named ? named[1].trim() : "");
      return `Pick ${val ? `"${val}"` : "a date"} in ${where ? `the ${where} calendar` : "the calendar"}`;
    }
    /* Pointing and values. A "kept:<list>" selector means every value an
       earlier keepValue step noted under that name. */
    case "hover":
      return `Point at ${field}`;
    case "wait":
      return `Wait ${val || "?"} seconds`;
    case "keepvalue":
      return `Note the value of ${field} as "${val || "values"}"`;
    case "expectlength":
    case "expectdistinct":
    case "expectcharsin": {
      const list = /^kept:\s*(.*)$/i.exec(s.selector || "");
      const what = list ? `every noted "${list[1] || "values"}" value` : `the value of ${field}`;
      if (action === "expectdistinct") return `Check that ${what.replace(/^every/, "the")}s all differ`;
      if (action === "expectcharsin") return `Check that every character of ${what} other than a letter or digit is one of "${val || ""}"`;
      const range = /^\s*(\d+)\s*(?:-|–|to)\s*(\d+)\s*$/i.exec(val || "");
      return `Check that ${what} has ${range ? `${range[1]} to ${range[2]}` : val} characters`;
    }
    case "expectcount": {
      const range = /^\s*(\d+)\s*(?:-|–|to)\s*(\d+)\s*$/i.exec(val || "");
      return `Check that ${range ? `${range[1]} to ${range[2]}` : val || "0"} of ${field} are on the page`;
    }
    case "expectreadonly":
      return `Check that ${field} cannot be changed`;
    case "expecteditable":
      return `Check that ${field} can be changed`;
    default:
      return val ? `${s.action} ${field} with "${val}"` : `${s.action} ${field}`;
  }
}

/* The technical line, shown in brackets under the sentence. Mirrors how the
   runner actually dispatches the step. */
export function stepToScript(s) {
  if (!s || typeof s !== "object") return "";
  const action = s.action || "";
  if (!action) return "";
  const bits = [action];
  if (s.selector) bits.push(s.selector);
  if (s.value != null && s.value !== "") {
    bits.push(`= ${s.masked ? '"••••••••"' : JSON.stringify(String(s.value))}`);
  }
  return bits.join(" ");
}

/* A Cucumber line with no Artifact step that only states set-up: a Given (or
   its And / But) before the first When / Then, on an Artifact that has
   preconditions. There is nothing to do on screen for it; the step flagged
   `precondition_check` is where a run checks it. Returns the note to show in
   place of "No Artifact step found.", or "" when the line is a real gap.
   `ordered` is the Artifact's steps in the order the screen numbers them.
   `preconditionLines`, saved at Generate, names those lines exactly; older
   Artifacts without it use the Given-block rule above. */
export function preconditionLineNote(line, lines, ordered, preconditions, preconditionLines) {
  if (!line?.keyword || !(preconditions || []).length) return "";
  if (Array.isArray(preconditionLines)) {
    if (!preconditionLines.includes(line.step_no)) return "";
  } else {
    const at = lines.indexOf(line);
    const firstAction = lines.findIndex((l) => /^(when|then)$/i.test(String(l?.keyword || "").trim()));
    if (at < 0 || (firstAction >= 0 && at >= firstAction)) return "";
  }
  const n = (ordered || []).findIndex((s) => s?.precondition_check) + 1;
  return n
    ? `Precondition: set up before the run, checked at step ${n} (Blocked if not met).`
    : "Precondition: set up before the run, nothing to do on screen.";
}

/* Why a step failed, in plain words, from its action and the browser's error
   (the same error kinds the runner's dry run tells apart). Returns
   {why, fault} — `fault` says where the problem most likely is: "artifact"
   (the script itself is wrong), "app" (the app did not do what the test
   expects), or "" when the error says too little to tell. */
export function explainFailure(step, error) {
  const err = String(error || "");
  const e = err.toLowerCase();
  const action = String(step?.action || "");
  const value = step?.value == null ? "" : String(step.value);
  const target = friendlySelector(step?.selector || "");
  const onPage = err.split(" — on the page right now: ")[1] || "";
  const seen = onPage ? ` On the page at that moment: ${onPage.trim()}` : "";

  if (/invalid url|cannot navigate/.test(e)) {
    return {
      why: `It tried to open "${value}", which is not a full web address (it needs https://…), so the browser could not go anywhere.`,
      fault: "artifact",
    };
  }
  if (e.startsWith("invalid selector") || /unexpected token|is not a valid selector|unknown engine/.test(e)) {
    return { why: `The way the step points at ${target} is written wrongly, so the element could not be looked up.`, fault: "artifact" };
  }
  if (/matched several elements|strict mode violation/.test(e)) {
    return {
      why: `More than one element on the page matches ${target}, so the step cannot tell which one to use. The Artifact needs a more specific pointer to it.`,
      fault: "artifact",
    };
  }
  if (/action does not fit|not an <input>|not a <select>|element is not/.test(e)) {
    return { why: `The step's action ("${action}") does not fit ${target} — for example typing into something that is not a field.`, fault: "artifact" };
  }
  if (/timeout|exceeded|waiting for/.test(e)) {
    const secs = (e.match(/timeout (\d+)ms/) || [])[1];
    const within = secs ? ` within ${Math.round(Number(secs) / 1000)} seconds` : "";
    if (action.startsWith("expect")) {
      return { why: `${cap(target)} did not reach the expected state${within}.${seen}`, fault: "app" };
    }
    return {
      why: `${cap(target)} was not found on the page${within}. It may not be on this screen, the page may not have got there, or the element has changed.${seen}`,
      fault: "",
    };
  }
  if (action.startsWith("expect")) {
    return { why: `The check “${stepToEnglish({ ...step, description: undefined })}” did not hold on the page.${seen}`, fault: "app" };
  }
  return { why: err ? `The browser reported an error while doing this step.${seen}` : "The step failed without an error message.", fault: "" };
}

function cap(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}
